/**
 * `<MechWearBlock>` — the WEAR & REPAIRS block on a mech sheet (`between-encounter-flow` 2.1–2.2):
 * the printed mech's carried wear (`PilotData.printedMechs`, "survives a Rest", `decisions/0036`)
 * over two sections, RAW's two ways to recover (Core p.82):
 *
 *  - **REST** (one hour): the pilot's half-HP `rest`, then the Repair spends grouped by cost (1, 2,
 *    and the GM's 4-Repair Restore wreck). Heat and statuses need no control: they end with the fight.
 *  - **FULL REPAIR** (ten hours, GM only): one big button that clears the mech's record AND the
 *    pilot's carried HP in one undo step (a `batch`), plays the FULL REPAIR banner (`onFullRepair`),
 *    plus Reprint for a destroyed mech.
 *
 * **Who sees it: the mech's owner and the GM, nobody else.** It renders nothing for any other
 * viewer, and SQUAD does not mount it at all (`SHARED_SHEET_DISCLOSURE.printedMechs` stays
 * `"hidden"`, `SquadApp.tsx`).
 *
 * **Every control is an existing roster command; nothing here is new rules.** The track spends
 * dispatch `spend_repairs` priced by `repairCost` with the maxima `printedMechMaxes` derives (the
 * same builder `lvtt roster repair` uses). Full Repair, Restore wreck and Reprint are the GM's.
 * The UI mirrors the reducer's rules as disabled-with-a-reason; the reducer stays the wall, and
 * a rejection that slips through is named inline. Decision 2 = A: the owner spends directly, and
 * each spend is an ordinary undoable command in the pilot's undo scope (`pilotScope`).
 *
 * **The Rest gate is "any active encounter"** (orchestrator ruling Q4): every control is disabled
 * while `activeEncounterId !== null`. A mech deployed into that encounter seeded its Repair Cap
 * from this record, so a spend now would be overwritten when the fight commits.
 */
import { useState } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import {
  CORE_POWER_POOL,
  pilotScope,
  repairCost,
  resolvePrintedMechValues,
  type PilotData,
  type RepairTrack,
  type RosterCommand,
} from "@lancer-vtt/domain";
import { printedMechMaxes } from "@lancer-vtt/rules";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import { limitedSystemName, printedItemName } from "../../lib/roster/printedMechLabels";
import "./mech-wear.css";

/** The talent that grants the 2-Repair Limited replenish (Unsanctioned Capacity Upgrade). */
const GREASE_MONKEY = "t_grease_monkey";

export const REST_LIVE_FIGHT_REASON = "A fight is live — rest after END COMBAT";

/**
 * What a FULL REPAIR takes and restores (Core p.82). RAW's term, not "long rest": a Rest is the
 * separate one-hour break.
 */
export const FULL_REPAIR_HINT = "10 hours in a secure location: full HP, structure, stress, repairs and limited uses";

/** What a REST takes and restores (Core p.82). */
export const REST_HINT = "1 hour of downtime: clear heat and statuses, restore half pilot HP and clear DOWN AND OUT, then spend Repairs";

/** One Rest control: what it costs, and why it is off (if it is). */
type Control = { key: string; label: string; cost: number; reason: string | null; command: RosterCommand };

export function MechWearBlock({
  pilot,
  mechId,
  compendium,
  roster,
  actorId,
  isGm,
  activeEncounterId,
  pilotMaxHp,
  onFullRepair,
}: {
  pilot: PilotData;
  mechId: string;
  compendium: Compendium;
  roster: Pick<UseRoster, "dispatch" | "undo" | "undoState">;
  /** The viewer's verified slot id. */
  actorId: string | null;
  isGm: boolean;
  /** `session.activeEncounterId`: any live encounter closes the Rest. */
  activeEncounterId: string | null;
  /** The pilot's computed max HP (`ComputedPilot.combat.hp`), which `rest` halves; unknown disables Rest. */
  pilotMaxHp: number | undefined;
  /** Play the FULL REPAIR banner after a Full Repair lands. */
  onFullRepair: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  if (!isGm && (actorId === null || pilot.owner !== actorId)) return null;
  const maxes = printedMechMaxes({ pilot, mechId, compendium });
  if (!maxes || !actorId) return null;

  const printed = pilot.printedMechs[mechId];
  const values = resolvePrintedMechValues(printed, maxes);
  const destroyed = printed?.destroyed;
  const destroyedItems = printed?.destroyedItems ?? [];
  const limitedUsed = Object.entries(printed?.limitedUsed ?? {});
  const limitedSystemUsed = Object.entries(printed?.limitedSystemUsed ?? {});
  const corePowerSpent = (printed?.limitedSystemUsed?.[CORE_POWER_POOL] ?? 0) > 0;
  // The Limited replenish skips Core Power (`replenishLimitedUsed`), so it needs another entry.
  const replenishable = limitedUsed.length > 0 || limitedSystemUsed.some(([k]) => k !== CORE_POWER_POOL);

  const live = activeEncounterId !== null;
  const ids = { actorId, pilotId: pilot.id, mechId };
  const spendMaxes = {
    maxHp: maxes.maxHp,
    maxStructure: maxes.maxStructure,
    maxStress: maxes.maxStress,
    repairCapMax: maxes.repairCapMax,
    ...(maxes.structureRepairCost !== undefined ? { structureRepairCost: maxes.structureRepairCost } : {}),
  };

  /** A track spend's reason to be off, most binding first. */
  const spendReason = (cost: number, nothingToDo: string | null): string | null => {
    if (live) return REST_LIVE_FIGHT_REASON;
    if (destroyed) return "Destroyed — restore or reprint it first";
    if (nothingToDo) return nothingToDo;
    if (values.repairsRemaining < cost) return `Needs ${cost} Repair${cost === 1 ? "" : "s"}, ${values.repairsRemaining} left`;
    return null;
  };
  const spend = (args: { track: RepairTrack; label: string; nothingToDo?: string | null; itemKey?: string }): Control => {
    const { track, label, nothingToDo = null, itemKey } = args;
    const cost = repairCost(track, maxes.structureRepairCost);
    return {
      key: itemKey ? `item:${itemKey}` : track,
      label,
      cost,
      reason: spendReason(cost, nothingToDo),
      command: { type: "spend_repairs", ...ids, track, ...spendMaxes, ...(itemKey !== undefined ? { itemKey } : {}) },
    };
  };

  const controls: Control[] = [
    spend({ track: "hp", label: "Restore HP", nothingToDo: values.hp >= maxes.maxHp ? "HP is full" : null }),
    spend({ track: "structure", label: "+1 Structure", nothingToDo: values.structure >= maxes.maxStructure ? "Structure is full" : null }),
    spend({ track: "stress", label: "+1 Stress", nothingToDo: values.stress >= maxes.maxStress ? "Stress is full" : null }),
    ...destroyedItems.map((key) => spend({ track: "item", label: `Repair ${printedItemName({ pilot, mechId, key, compendium })}`, itemKey: key })),
    ...((pilot.talents[GREASE_MONKEY] ?? 0) >= 1 && replenishable ? [spend({ track: "limited", label: "Replenish Limited" })] : []),
  ];

  const gmControls: Control[] = !isGm
    ? []
    : [
        ...(destroyed === "wreck"
          ? [
              {
                key: "restore",
                label: "Restore wreck",
                cost: 4,
                reason: live
                  ? REST_LIVE_FIGHT_REASON
                  : values.repairsRemaining < 4
                    ? `Needs 4 Repairs, ${values.repairsRemaining} left (pool across pilots with lvtt roster restore-wreck)`
                    : null,
                command: {
                  type: "restore_destroyed_mech",
                  ...ids,
                  contributions: [{ pilotId: pilot.id, mechId, repairs: 4, repairCapMax: maxes.repairCapMax }],
                },
              } satisfies Control,
            ]
          : []),
      ];

  // REST's own control: the pilot's half HP back (Core p.82).
  const pilotHp = pilot.currentHp;
  const restAfter = pilotHp !== undefined && pilotMaxHp !== undefined ? Math.min(pilotMaxHp, pilotHp + Math.ceil(pilotMaxHp / 2)) : undefined;
  const restControl: Control = {
    key: "rest",
    label: pilotHp !== undefined && pilotMaxHp !== undefined ? `Rest · pilot HP ${pilotHp}/${pilotMaxHp} → ${restAfter}/${pilotMaxHp}` : "Rest",
    cost: 0,
    reason: live ? REST_LIVE_FIGHT_REASON : pilotMaxHp === undefined ? "Pilot HP unknown" : pilotHp === undefined || pilotHp >= pilotMaxHp ? "Pilot HP is full" : null,
    command: { type: "rest", actorId, pilotId: pilot.id, maxHp: pilotMaxHp ?? 0 },
  };

  // FULL REPAIR mends the mech and the pilot (RAW: "restore all pilot HP") in one undo step.
  const fullRepairCommand: RosterCommand =
    pilotHp === undefined
      ? { type: "full_repair", ...ids }
      : { type: "batch", commands: [{ type: "full_repair", ...ids }, { type: "set_pilot_hp", actorId, pilotId: pilot.id }] };
  const fullControl: Control = {
    key: "full",
    label: "FULL REPAIR",
    cost: 0,
    reason: live ? REST_LIVE_FIGHT_REASON : destroyed === "vaporized" ? "Vaporized — only a reprint clears it" : !printed && pilotHp === undefined ? "Already fully repaired" : null,
    command: fullRepairCommand,
  };
  const reprintControl: Control | null = destroyed
    ? { key: "reprint", label: "Reprint", cost: 0, reason: live ? REST_LIVE_FIGHT_REASON : null, command: { type: "reprint", ...ids } }
    : null;

  const run = (command: RosterCommand, after?: () => void) => {
    const res = roster.dispatch(command);
    setError(res.ok ? null : res.error);
    if (res.ok) after?.();
  };
  const scope = pilotScope(pilot.id);
  const undo = roster.undoState(scope);

  const track = (label: string, current: number, max: number) => (
    <div className="mwb-track" data-track={label}>
      <div className="mwb-value">
        {current}/{max}
      </div>
      <div className="mwb-label">{label}</div>
    </div>
  );

  const button = (c: Control, className = "mwb-btn", after?: () => void) => (
    <button
      key={c.key}
      type="button"
      className={className}
      data-control={c.key}
      disabled={c.reason !== null}
      title={c.reason ?? undefined}
      onClick={() => run(c.command, after)}
    >
      {c.label}
    </button>
  );

  // The Repair spends, grouped by what RAW charges (Core p.82).
  const spends = [...controls, ...gmControls];
  const tiers = [...new Set(spends.map((c) => c.cost))].sort((a, b) => a - b);

  return (
    <div className="mwb" data-testid="mech-wear">
      <div className="mwb-head">
        <span>WEAR &amp; REPAIRS</span>
        <button
          type="button"
          className="mwb-btn"
          disabled={!undo.canUndo}
          title={undo.undoLabel ?? undefined}
          onClick={() => roster.undo(scope)}
        >
          Undo
        </button>
      </div>
      {destroyed && <div className="mwb-destroyed">{destroyed === "vaporized" ? "VAPORIZED — reprint to recover" : "DESTROYED — WRECK"}</div>}
      <div className="mwb-tracks">
        {track("HP", values.hp, maxes.maxHp)}
        {track("STRUCTURE", values.structure, maxes.maxStructure)}
        {track("STRESS", values.stress, maxes.maxStress)}
        {track("REPAIRS", values.repairsRemaining, maxes.repairCapMax)}
      </div>
      {(destroyedItems.length > 0 || limitedUsed.length > 0 || limitedSystemUsed.length > 0) && (
        <ul className="mwb-items">
          {destroyedItems.map((key) => (
            <li key={`d-${key}`}>DESTROYED · {printedItemName({ pilot, mechId, key, compendium })}</li>
          ))}
          {limitedUsed.map(([key, n]) => (
            <li key={`l-${key}`}>
              LIMITED · {printedItemName({ pilot, mechId, key, compendium })} · {n} used
            </li>
          ))}
          {limitedSystemUsed
            .filter(([key]) => key !== CORE_POWER_POOL)
            .map(([key, n]) => (
              <li key={`s-${key}`}>
                LIMITED · {limitedSystemName(compendium, key)} · {n} used
              </li>
            ))}
          {corePowerSpent && <li>CORE POWER SPENT</li>}
        </ul>
      )}
      <section className="mwb-section" data-section="rest">
        <div className="mwb-section-head">
          <span>REST</span>
          <span className="mwb-section-time">1 HOUR</span>
        </div>
        <div className="mwb-hint">{REST_HINT}</div>
        <div className="mwb-controls">{button(restControl)}</div>
        {tiers.map((cost) => (
          <div key={cost} className="mwb-tier">
            <span className="mwb-tier-cost">
              {cost} REPAIR{cost === 1 ? "" : "S"}
            </span>
            <div className="mwb-controls">{spends.filter((c) => c.cost === cost).map((c) => button(c))}</div>
          </div>
        ))}
      </section>
      {isGm && (
        <section className="mwb-section mwb-section--full" data-section="full-repair">
          <div className="mwb-section-head">
            <span>FULL REPAIR</span>
            <span className="mwb-section-time">10 HOURS</span>
          </div>
          {button(fullControl, "mwb-bigbtn", onFullRepair)}
          <div className="mwb-hint">{FULL_REPAIR_HINT}</div>
          {reprintControl && <div className="mwb-controls">{button(reprintControl)}</div>}
        </section>
      )}
      {live && <div className="mwb-note">{REST_LIVE_FIGHT_REASON}</div>}
      {error && (
        <div className="mwb-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
